import { useState, useEffect } from 'react'
import axios from 'axios'
import logoWellbeing24 from '../assets/logo-wellbeing24.svg'

interface ThemeConfig {
  [key: string]: any
}

interface ThemeLogo {
  url: string
  fileName: string
  mimeType: string
}

// Map of domain to logo asset
const themeLogos: Record<string, string> = {
  'support.wellbeing24.org': logoWellbeing24,
  'wellbeing24.org': logoWellbeing24
}

interface TenantTheme {
  success: boolean
  tenant: {
    id: string
    name: string
    domain: string
    clientOrgId: string | null
    clientOrgName: string | null
  } | null
  theme: {
    id: string
    name: string
    version: string
  } | null
  logo: ThemeLogo | null
  configurations: ThemeConfig
  customizations: ThemeConfig | null
}

export const useTheme = () => {
  const [theme, setTheme] = useState<TenantTheme | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Get current domain
        const domain = window.location.hostname
        
        // Check if we have a local logo asset for this domain
        const localLogo = themeLogos[domain]
        
        // Fetch theme based on current domain
        // Suppress errors for this call - failures are handled gracefully
        const response = await axios.get<TenantTheme>('/api/tenant/theme', {
          validateStatus: () => true, // Don't throw on any status code
          skipErrorHandler: true // Skip global error handlers
        } as any)
        
        // Handle response manually
        let themeData: TenantTheme
        if (response.status === 200 && response.data) {
          themeData = response.data
          
          // Override logo URL with local asset if available
          if (localLogo && themeData.tenant) {
            themeData.logo = {
              url: localLogo,
              fileName: 'logo.svg',
              mimeType: 'image/svg+xml'
            }
          }
        } else {
          // Silently handle errors - set default theme
          themeData = {
            success: false,
            tenant: null,
            theme: null,
            logo: null,
            configurations: {},
            customizations: null
          }
        }
        
        setTheme(themeData)
      } catch (err: any) {
        // Network errors only - should be rare with validateStatus
        console.debug('Theme fetch network error (handled silently):', err.message)
        // Set default theme on error
        setTheme({
          success: false,
          tenant: null,
          theme: null,
          logo: null,
          configurations: {},
          customizations: null
        })
      } finally {
        setLoading(false)
      }
    }

    fetchTheme()
  }, [])

  return { theme, loading, error }
}


